package com.evoapps.evoshift.domain.usecase.requests

import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.requests.LimitCheck
import com.evoapps.evoshift.domain.usecase.ResolveAbsenceConflictsUseCase
import com.evoapps.evoshift.domain.util.AppClock

/** SH-224: what a manager's decision on an absence came to. */
sealed interface AbsenceDecision {
    /** Saved as [absence]. */
    data class Decided(val absence: Absence) : AbsenceDecision

    /** Not saved: the approval goes over a yearly limit ([check]) and needs the manager's note. */
    data class OverLimit(val check: LimitCheck.Exceeds) : AbsenceDecision
}

/** SH-224: approving freed none of the person's shifts because freeing them failed ([cause]). */
class ResolveConflictsFailedException(cause: Throwable) : Exception("The absence's shifts could not be freed", cause)

/**
 * SH-223's decision on an absence, shared by the Requests tab and the timeline (SH-224): an
 * approval is checked again against the yearly limits (over one only with [overrideNote]), frees
 * the shifts it overlaps (part of a day: only those), then the request is saved approved; a
 * rejection is saved with its optional reason.
 */
class DecideAbsenceUseCase(
    private val absenceRepository: AbsenceRepository,
    private val resolveAbsenceConflicts: ResolveAbsenceConflictsUseCase,
    private val checkApprovalLimit: CheckApprovalLimitUseCase? = null
) {
    suspend operator fun invoke(
        absence: Absence,
        status: AbsenceStatus,
        managerId: String,
        managerName: String,
        /** The workplace whose shifts an approval frees (the absence's own when null). */
        workplaceId: String? = absence.workplaceId,
        rejectionReason: String? = null,
        overrideNote: String? = null
    ): Result<AbsenceDecision> {
        if (status == AbsenceStatus.PENDING) return Result.failure(IllegalArgumentException("A decision approves or rejects"))
        return runCatching {
            if (status == AbsenceStatus.APPROVED) {
                if (overrideNote == null) {
                    val check = checkApprovalLimit?.invoke(absence)
                    if (check is LimitCheck.Exceeds) return Result.success(AbsenceDecision.OverLimit(check))
                }
                val where = workplaceId ?: throw IllegalStateException("No workplace to free the shifts at")
                resolveAbsenceConflicts(where, absence).onFailure { throw ResolveConflictsFailedException(it) }
            }
            val now = AppClock.now().toString()
            val updated = absence.copy(
                status = status,
                rejectionReason = rejectionReason.takeIf { status == AbsenceStatus.REJECTED },
                overrideNote = overrideNote.takeIf { status == AbsenceStatus.APPROVED },
                processedBy = managerId,
                processedAt = now,
                metadata = absence.metadata.copy(updatedAt = now, updatedBy = managerName)
            )
            absenceRepository.updateAbsence(updated).getOrThrow()
            AbsenceDecision.Decided(updated)
        }
    }
}
